import {
  ActionTypes as DueDiligenceEditActionTypes,
  ISaveDueDiligenceItemSuccessAction,
} from '@app/components/DueDiligenceItemEdit/modules/dueDiligenceItemEdit'
import { IAttachment } from '@app/models/Attachment'
import { CityProfile, ICityProfile } from '@app/models/CityProfile'
import {
  DevelopmentCriticalDatesDocument,
  IDevelopmentCriticalDatesDocument,
} from '@app/models/DevelopmentCriticalDatesDocument'
import { DevelopmentSite, IDevelopmentSite } from '@app/models/DevelopmentSite'
import {
  DueDiligenceItem,
  IDueDiligenceItem,
} from '@app/models/DueDiligenceItem'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { IShareContact } from '@app/models/ShareContact'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IDevelopmentSiteViewState {
  readonly cityProfile?: ICityProfile
  readonly criticalDatesDoc?: IDevelopmentCriticalDatesDocument
  readonly dueDiligenceItems?: List<IDueDiligenceItem>
  readonly error?: string
  readonly isDeleting: boolean
  readonly isLoading: boolean
  readonly isLoadingCityProfile: boolean
  readonly isLoadingCriticalDatesDoc: boolean
  readonly isLoadingDueDiligenceItems: boolean
  readonly isSharingCriticalDates: boolean
  readonly permissions: IEntityPermissions
  readonly showConfirmDelete: boolean
  readonly showShareCriticalDatesModal: boolean
  readonly site: IDevelopmentSite
}

const DevelopmentSiteViewStateRecord = Record({
  cityProfile: undefined,
  criticalDatesDoc: undefined,
  dueDiligenceItems: undefined,
  error: undefined,
  isDeleting: false,
  isLoading: false,
  isLoadingCityProfile: false,
  isLoadingCriticalDatesDoc: false,
  isLoadingDueDiligenceItems: false,
  isSharingCriticalDates: false,
  permissions: new EntityPermissions(),
  showConfirmDelete: false,
  showShareCriticalDatesModal: false,
  site: new DevelopmentSite(),
})

export class DevelopmentSiteViewState extends DevelopmentSiteViewStateRecord
  implements IDevelopmentSiteViewState {
  public static fromJS(initialState?: Partial<IDevelopmentSiteViewState>) {
    if (!initialState) {
      return new DevelopmentSiteViewState(initialState)
    }

    const {
      cityProfile,
      criticalDatesDoc,
      dueDiligenceItems,
      permissions,
      site,
      ...rest
    } = initialState
    return new DevelopmentSiteViewState({
      cityProfile: cityProfile ? CityProfile.fromJS(cityProfile) : undefined,
      criticalDatesDoc: criticalDatesDoc
        ? DevelopmentCriticalDatesDocument.fromJS(criticalDatesDoc)
        : undefined,
      dueDiligenceItems: dueDiligenceItems
        ? List(dueDiligenceItems.map((d) => DueDiligenceItem.fromJS(d)))
        : undefined,
      permissions: EntityPermissions.fromJS(permissions),
      site: DevelopmentSite.fromJS(site),
      ...rest,
    })
  }

  public readonly cityProfile?: CityProfile
  public readonly criticalDatesDoc?: DevelopmentCriticalDatesDocument
  public readonly dueDiligenceItems?: List<DueDiligenceItem>
  public readonly error?: string
  public readonly isDeleting: boolean
  public readonly isLoading: boolean
  public readonly isLoadingCityProfile: boolean
  public readonly isLoadingCriticalDatesDoc: boolean
  public readonly isLoadingDueDiligenceItems: boolean
  public readonly isSharingCriticalDates: boolean
  public readonly permissions: EntityPermissions
  public readonly showConfirmDelete: boolean
  public readonly showShareCriticalDatesModal: boolean
  public readonly site: DevelopmentSite

  constructor(props?: Partial<DevelopmentSiteViewState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  CloseShareCriticalDates = 'DEVELOPMENT_SITE::CLOSE_SHARE_CRITICAL_DATES',
  DeleteDevelopmentSite = 'DEVELOPMENT_SITE::DELETE',
  DeleteDevelopmentSiteFailure = 'DEVELOPMENT_SITE::DELETE_FAILURE',
  DeleteDevelopmentSiteRequest = 'DEVELOPMENT_SITE::DELETE_REQUEST',
  DeleteDevelopmentSiteSuccess = 'DEVELOPMENT_SITE::DELETE_SUCCESS',
  DeleteDueDiligenceItem = 'DEVELOPMENT_SITE::DELETE_DUE_DILIGENCE_ITEM',
  DeleteDueDiligenceItemFailure = 'DEVELOPMENT_SITE::DELETE_DUE_DILIGENCE_ITEM_FAILURE',
  DeleteDueDiligenceItemRequest = 'DEVELOPMENT_SITE::DELETE_DUE_DILIGENCE_ITEM_REQUEST',
  DeleteDueDiligenceItemSuccess = 'DEVELOPMENT_SITE::DELETE_DUE_DILIGENCE_ITEM_SUCCESS',
  GetSiteRouting = 'DEVELOPMENT_SITE::GET_SITE_ROUTING',
  LoadCityProfile = 'DEVELOPMENT_SITE::LOAD_CITY_PROFILE',
  LoadCityProfileFailure = 'DEVELOPMENT_SITE::LOAD_CITY_PROFILE_FAILURE',
  LoadCityProfileRequest = 'DEVELOPMENT_SITE::LOAD_CITY_PROFILE_REQUEST',
  LoadCityProfileSuccess = 'DEVELOPMENT_SITE::LOAD_CITY_PROFILE_SUCCESS',
  LoadCriticalDatesDocument = 'DEVELOPMENT_SITE::LOAD_CRITICAL_DATES_DOCUMENT',
  LoadCriticalDatesDocumentFailure = 'DEVELOPMENT_SITE::LOAD_CRITICAL_DATES_DOCUMENT_FAILURE',
  LoadCriticalDatesDocumentRequest = 'DEVELOPMENT_SITE::LOAD_CRITICAL_DATES_DOCUMENT_REQUEST',
  LoadCriticalDatesDocumentSuccess = 'DEVELOPMENT_SITE::LOAD_CRITICAL_DATES_DOCUMENT_SUCCESS',
  LoadDevelopmentSite = 'DEVELOPMENT_SITE::LOAD',
  LoadDevelopmentSiteFailure = 'DEVELOPMENT_SITE::LOAD_FAILURE',
  LoadDevelopmentSiteRequest = 'DEVELOPMENT_SITE::LOAD_REQUEST',
  LoadDevelopmentSiteSuccess = 'DEVELOPMENT_SITE::LOAD_SUCCESS',
  LoadDueDiligenceItems = 'DEVELOPMENT_SITE::LOAD_DUE_DILIGENCE_ITEMS',
  LoadDueDiligenceItemsFailure = 'DEVELOPMENT_SITE::LOAD_DUE_DILIGENCE_ITEMS_FAILURE',
  LoadDueDiligenceItemsRequest = 'DEVELOPMENT_SITE::LOAD_DUE_DILIGENCE_ITEMS_REQUEST',
  LoadDueDiligenceItemsSuccess = 'DEVELOPMENT_SITE::LOAD_DUE_DILIGENCE_ITEMS_SUCCESS',
  ResetState = 'DEVELOPMENT_SITE::RESET_STATE',
  SetAttachments = 'DEVELOPMENT_SITE::SET_ATTACHMENTS',
  SetCityProfileAttachments = 'DEVELOPMENT_SITE::SET_CITY_PROFILE_ATTACHMENTS',
  ShareCriticalDates = 'DEVELOPMENT_SITE::SHARE_CRITICAL_DATES',
  ShareCriticalDatesFailure = 'DEVELOPMENT_SITE::SHARE_CRITICAL_DATES_FAILURE',
  ShareCriticalDatesRequest = 'DEVELOPMENT_SITE::SHARE_CRITICAL_DATES_REQUEST',
  ShareCriticalDatesSuccess = 'DEVELOPMENT_SITE::SHARE_CRITICAL_DATES_SUCCESS',
  ShowShareCriticalDates = 'DEVELOPMENT_SITE::SHOW_SHARE_CRITICAL_DATES',
  ToggleConfirmDelete = 'DEVELOPMENT_SITE::TOGGLE_CONFIRM_DELETE',
  UpdateDueDiligenceItem = 'DEVELOPMENT_SITE::UPDATE_DUE_DILIGENCE_ITEM',
  UpdateDueDiligenceItemFailure = 'DEVELOPMENT_SITE::UPDATE_DUE_DILIGENCE_ITEM_FAILURE',
  UpdateDueDiligenceItemRequest = 'DEVELOPMENT_SITE::UPDATE_DUE_DILIGENCE_ITEM_REQUEST',
  UpdateDueDiligenceItemSuccess = 'DEVELOPMENT_SITE::UPDATE_DUE_DILIGENCE_ITEM_SUCCESS',
  SaveCriticalDatesEditSuccess = 'CRITICAL_DATES_EDIT::SAVE_SUCCESS',
}

interface ICloseShareCriticalDatesAction {
  type: ActionTypes.CloseShareCriticalDates
}

interface IDeleteDevelopmentSiteAction {
  clientId: string
  siteId: string
  type: ActionTypes.DeleteDevelopmentSite
}

interface IDeleteDevelopmentSiteFailureAction {
  error: string
  type: ActionTypes.DeleteDevelopmentSiteFailure
}

interface IDeleteDevelopmentSiteRequestAction {
  type: ActionTypes.DeleteDevelopmentSiteRequest
}

interface IDeleteDevelopmentSiteSuccessAction {
  type: ActionTypes.DeleteDevelopmentSiteSuccess
}

interface IDeleteDueDiligenceItemAction {
  itemId: string
  type: ActionTypes.DeleteDueDiligenceItem
}

interface IDeleteDueDiligenceItemFailureAction {
  error: string
  type: ActionTypes.DeleteDueDiligenceItemFailure
}

interface IDeleteDueDiligenceItemRequestAction {
  type: ActionTypes.DeleteDueDiligenceItemRequest
}

interface IDeleteDueDiligenceItemSuccessAction {
  itemId: string
  type: ActionTypes.DeleteDueDiligenceItemSuccess
}

interface IGetSiteRoutingAction {
  siteId: string
  type: ActionTypes.GetSiteRouting
}

interface ILoadCityProfileAction {
  placeFIPS: string
  stateFIPS: string
  type: ActionTypes.LoadCityProfile
}

interface ILoadCityProfileFailureAction {
  error: string
  type: ActionTypes.LoadCityProfileFailure
}

interface ILoadCityProfileRequestAction {
  type: ActionTypes.LoadCityProfileRequest
}

interface ILoadCityProfileSuccessAction {
  cityProfile: CityProfile
  type: ActionTypes.LoadCityProfileSuccess
}

interface ILoadCriticalDatesDocumentAction {
  siteId: string
  type: ActionTypes.LoadCriticalDatesDocument
}

interface ILoadCriticalDatesDocumentFailureAction {
  error: string
  type: ActionTypes.LoadCriticalDatesDocumentFailure
}

interface ILoadCriticalDatesDocumentRequestAction {
  type: ActionTypes.LoadCriticalDatesDocumentRequest
}

interface ILoadCriticalDatesDocumentSuccessAction {
  criticalDatesDoc: DevelopmentCriticalDatesDocument
  type: ActionTypes.LoadCriticalDatesDocumentSuccess
}

interface ILoadDevelopmentSiteAction {
  clientSlug: string
  siteSlug: string
  type: ActionTypes.LoadDevelopmentSite
}

interface ILoadDevelopmentSiteFailureAction {
  error: string
  type: ActionTypes.LoadDevelopmentSiteFailure
}

interface ILoadDevelopmentSiteRequestAction {
  type: ActionTypes.LoadDevelopmentSiteRequest
}

interface ILoadDevelopmentSiteSuccessAction {
  permissions: EntityPermissions
  site: DevelopmentSite
  type: ActionTypes.LoadDevelopmentSiteSuccess
}

interface ILoadDueDiligenceItemsAction {
  siteId: string
  type: ActionTypes.LoadDueDiligenceItems
}

interface ILoadDueDiligenceItemsFailureAction {
  error: string
  type: ActionTypes.LoadDueDiligenceItemsFailure
}

interface ILoadDueDiligenceItemsRequestAction {
  type: ActionTypes.LoadDueDiligenceItemsRequest
}

interface ILoadDueDiligenceItemsSuccessAction {
  dueDiligenceItems?: List<DueDiligenceItem>
  type: ActionTypes.LoadDueDiligenceItemsSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISetAttachmentsAction {
  type: ActionTypes.SetAttachments
  value: List<IAttachment>
}

interface ISetCityProfileAttachmentsAction {
  type: ActionTypes.SetCityProfileAttachments
  value: List<IAttachment>
}

interface IShareCriticalDatesAction {
  comment: string
  contacts: List<IShareContact>
  siteId: string
  type: ActionTypes.ShareCriticalDates
}

interface IShareCriticalDatesFailureAction {
  error: string
  type: ActionTypes.ShareCriticalDatesFailure
}

interface IShareCriticalDatesRequestAction {
  type: ActionTypes.ShareCriticalDatesRequest
}

interface IShareCriticalDatesSuccessAction {
  type: ActionTypes.ShareCriticalDatesSuccess
}

interface IShowShareCriticalDatesAction {
  type: ActionTypes.ShowShareCriticalDates
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

interface IUpdateDueDiligenceItemAction {
  index: number
  itemId: string
  siteId: string
  type: ActionTypes.UpdateDueDiligenceItem
}

interface IUpdateDueDiligenceItemFailureAction {
  error: string
  type: ActionTypes.UpdateDueDiligenceItemFailure
}

interface IUpdateDueDiligenceItemRequestAction {
  index: number
  itemId: string
  type: ActionTypes.UpdateDueDiligenceItemRequest
}

interface IUpdateDueDiligenceItemSuccessAction {
  index: number
  itemId: string
  siteId: string
  type: ActionTypes.UpdateDueDiligenceItemSuccess
}

interface ISaveCriticalDatesEditSuccessAction {
  criticalDates: List<IDueDiligenceItem>
  type: ActionTypes.SaveCriticalDatesEditSuccess
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICloseShareCriticalDatesAction
  | IDeleteDevelopmentSiteFailureAction
  | IDeleteDevelopmentSiteRequestAction
  | IDeleteDevelopmentSiteSuccessAction
  | IDeleteDueDiligenceItemFailureAction
  | IDeleteDueDiligenceItemRequestAction
  | IDeleteDueDiligenceItemSuccessAction
  | ILoadCityProfileFailureAction
  | ILoadCityProfileRequestAction
  | ILoadCityProfileSuccessAction
  | ILoadCriticalDatesDocumentFailureAction
  | ILoadCriticalDatesDocumentRequestAction
  | ILoadCriticalDatesDocumentSuccessAction
  | ILoadDevelopmentSiteFailureAction
  | ILoadDevelopmentSiteRequestAction
  | ILoadDevelopmentSiteSuccessAction
  | ILoadDueDiligenceItemsFailureAction
  | ILoadDueDiligenceItemsRequestAction
  | ILoadDueDiligenceItemsSuccessAction
  | IResetStateAction
  | ISaveDueDiligenceItemSuccessAction
  | ISetAttachmentsAction
  | ISetCityProfileAttachmentsAction
  | IShareCriticalDatesFailureAction
  | IShareCriticalDatesRequestAction
  | IShareCriticalDatesSuccessAction
  | IShowShareCriticalDatesAction
  | IToggleConfirmDeleteAction
  | IUpdateDueDiligenceItemFailureAction
  | IUpdateDueDiligenceItemRequestAction
  | IUpdateDueDiligenceItemSuccessAction
  | ISaveCriticalDatesEditSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------
function closeShareCriticalDates(): ICloseShareCriticalDatesAction {
  return { type: ActionTypes.CloseShareCriticalDates }
}

function deleteDevelopmentSite(
  clientId: string,
  siteId: string,
): IDeleteDevelopmentSiteAction {
  return {
    clientId,
    siteId,
    type: ActionTypes.DeleteDevelopmentSite,
  }
}

function deleteDevelopmentSiteFailure(
  error: string,
): IDeleteDevelopmentSiteFailureAction {
  return {
    error,
    type: ActionTypes.DeleteDevelopmentSiteFailure,
  }
}

function deleteDevelopmentSiteRequest(): IDeleteDevelopmentSiteRequestAction {
  return {
    type: ActionTypes.DeleteDevelopmentSiteRequest,
  }
}

function deleteDevelopmentSiteSuccess(): IDeleteDevelopmentSiteSuccessAction {
  return {
    type: ActionTypes.DeleteDevelopmentSiteSuccess,
  }
}

function deleteDueDiligenceItem(itemId: string): IDeleteDueDiligenceItemAction {
  return {
    itemId,
    type: ActionTypes.DeleteDueDiligenceItem,
  }
}

function deleteDueDiligenceItemFailure(
  error: string,
): IDeleteDueDiligenceItemFailureAction {
  return {
    error,
    type: ActionTypes.DeleteDueDiligenceItemFailure,
  }
}

function deleteDueDiligenceItemRequest(): IDeleteDueDiligenceItemRequestAction {
  return {
    type: ActionTypes.DeleteDueDiligenceItemRequest,
  }
}

function deleteDueDiligenceItemSuccess(
  itemId: string,
): IDeleteDueDiligenceItemSuccessAction {
  return {
    itemId,
    type: ActionTypes.DeleteDueDiligenceItemSuccess,
  }
}

function getSiteRouting(siteId: string): IGetSiteRoutingAction {
  return {
    siteId,
    type: ActionTypes.GetSiteRouting,
  }
}

function loadCityProfile(
  placeFIPS: string,
  stateFIPS: string,
): ILoadCityProfileAction {
  return {
    placeFIPS,
    stateFIPS,
    type: ActionTypes.LoadCityProfile,
  }
}

function loadCityProfileFailure(error: string): ILoadCityProfileFailureAction {
  return {
    error,
    type: ActionTypes.LoadCityProfileFailure,
  }
}

function loadCityProfileRequest(): ILoadCityProfileRequestAction {
  return {
    type: ActionTypes.LoadCityProfileRequest,
  }
}

function loadCityProfileSuccess(
  cityProfile: CityProfile,
): ILoadCityProfileSuccessAction {
  return {
    cityProfile,
    type: ActionTypes.LoadCityProfileSuccess,
  }
}

function loadCriticalDatesDocument(
  siteId: string,
): ILoadCriticalDatesDocumentAction {
  return {
    siteId,
    type: ActionTypes.LoadCriticalDatesDocument,
  }
}

function loadCriticalDatesDocumentFailure(
  error: string,
): ILoadCriticalDatesDocumentFailureAction {
  return {
    error,
    type: ActionTypes.LoadCriticalDatesDocumentFailure,
  }
}

function loadCriticalDatesDocumentRequest(): ILoadCriticalDatesDocumentRequestAction {
  return {
    type: ActionTypes.LoadCriticalDatesDocumentRequest,
  }
}

function loadCriticalDatesDocumentSuccess(
  criticalDatesDoc: DevelopmentCriticalDatesDocument,
): ILoadCriticalDatesDocumentSuccessAction {
  return {
    criticalDatesDoc,
    type: ActionTypes.LoadCriticalDatesDocumentSuccess,
  }
}

function loadDevelopmentSite(
  clientSlug: string,
  siteSlug: string,
): ILoadDevelopmentSiteAction {
  return {
    clientSlug,
    siteSlug,
    type: ActionTypes.LoadDevelopmentSite,
  }
}

function loadDevelopmentSiteFailure(
  error: string,
): ILoadDevelopmentSiteFailureAction {
  return {
    error,
    type: ActionTypes.LoadDevelopmentSiteFailure,
  }
}

function loadDevelopmentSiteRequest(): ILoadDevelopmentSiteRequestAction {
  return {
    type: ActionTypes.LoadDevelopmentSiteRequest,
  }
}

function loadDevelopmentSiteSuccess(
  site: DevelopmentSite,
  permissions: EntityPermissions,
): ILoadDevelopmentSiteSuccessAction {
  return {
    permissions,
    site,
    type: ActionTypes.LoadDevelopmentSiteSuccess,
  }
}

function loadDueDiligenceItems(siteId: string): ILoadDueDiligenceItemsAction {
  return {
    siteId,
    type: ActionTypes.LoadDueDiligenceItems,
  }
}

function loadDueDiligenceItemsFailure(
  error: string,
): ILoadDueDiligenceItemsFailureAction {
  return {
    error,
    type: ActionTypes.LoadDueDiligenceItemsFailure,
  }
}

function loadDueDiligenceItemsRequest(): ILoadDueDiligenceItemsRequestAction {
  return {
    type: ActionTypes.LoadDueDiligenceItemsRequest,
  }
}

function loadDueDiligenceItemsSuccess(
  dueDiligenceItems?: List<DueDiligenceItem>,
): ILoadDueDiligenceItemsSuccessAction {
  return {
    dueDiligenceItems,
    type: ActionTypes.LoadDueDiligenceItemsSuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function setAttachments(value: List<IAttachment>): ISetAttachmentsAction {
  return {
    type: ActionTypes.SetAttachments,
    value,
  }
}

function setCityProfileAttachments(
  value: List<IAttachment>,
): ISetCityProfileAttachmentsAction {
  return {
    type: ActionTypes.SetCityProfileAttachments,
    value,
  }
}

function shareCriticalDates(
  siteId: string,
  contacts: List<IShareContact>,
  comment: string,
): IShareCriticalDatesAction {
  return {
    comment,
    contacts,
    siteId,
    type: ActionTypes.ShareCriticalDates,
  }
}

function shareCriticalDatesFailure(
  error: string,
): IShareCriticalDatesFailureAction {
  return {
    error,
    type: ActionTypes.ShareCriticalDatesFailure,
  }
}

function shareCriticalDatesRequest(): IShareCriticalDatesRequestAction {
  return { type: ActionTypes.ShareCriticalDatesRequest }
}

function shareCriticalDatesSuccess(): IShareCriticalDatesSuccessAction {
  return { type: ActionTypes.ShareCriticalDatesSuccess }
}

function showShareCriticalDates(): IShowShareCriticalDatesAction {
  return { type: ActionTypes.ShowShareCriticalDates }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

function updateDueDiligenceItem(
  itemId: string,
  siteId: string,
  index: number,
): IUpdateDueDiligenceItemAction {
  return {
    index,
    itemId,
    siteId,
    type: ActionTypes.UpdateDueDiligenceItem,
  }
}

function updateDueDiligenceItemFailure(
  error: string,
): IUpdateDueDiligenceItemFailureAction {
  return {
    error,
    type: ActionTypes.UpdateDueDiligenceItemFailure,
  }
}

function updateDueDiligenceItemRequest(
  index: number,
  itemId: string,
): IUpdateDueDiligenceItemRequestAction {
  return {
    index,
    itemId,
    type: ActionTypes.UpdateDueDiligenceItemRequest,
  }
}

function updateDueDiligenceItemSuccess(
  index: number,
  itemId: string,
  siteId: string,
): IUpdateDueDiligenceItemSuccessAction {
  return {
    index,
    itemId,
    siteId,
    type: ActionTypes.UpdateDueDiligenceItemSuccess,
  }
}

function saveEditCriticalDatesSuccess(
  criticalDates: List<IDueDiligenceItem>,
): ISaveCriticalDatesEditSuccessAction {
  return {
    criticalDates,
    type: ActionTypes.SaveCriticalDatesEditSuccess,
  }
}

export const actionCreators = {
  closeShareCriticalDates,
  deleteDevelopmentSite,
  deleteDueDiligenceItem,
  getSiteRouting,
  loadCityProfile,
  loadCriticalDatesDocument,
  loadDevelopmentSite,
  loadDueDiligenceItems,
  resetState,
  saveEditCriticalDatesSuccess,
  setAttachments,
  setCityProfileAttachments,
  shareCriticalDates,
  showShareCriticalDates,
  toggleConfirmDelete,
  updateDueDiligenceItem,
}

export const siteRoutingActionCreators = {
  getSiteRouting,
}

// -------------------------
// REDUCER
// -------------------------

const initState = new DevelopmentSiteViewState()

export const reducer: Reducer<DevelopmentSiteViewState> = (
  state: DevelopmentSiteViewState = initState,
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.SaveCriticalDatesEditSuccess:
      return state.setIn(
        ['criticalDatesDoc', 'criticalDates'],
        action.criticalDates,
      ) as DevelopmentSiteViewState

    case ActionTypes.CloseShareCriticalDates:
      return state.set(
        'showShareCriticalDatesModal',
        false,
      ) as DevelopmentSiteViewState

    case ActionTypes.DeleteDevelopmentSiteFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as DevelopmentSiteViewState

    case ActionTypes.DeleteDevelopmentSiteRequest:
      return state.set('isDeleting', true) as DevelopmentSiteViewState

    case ActionTypes.DeleteDevelopmentSiteSuccess:
      return state
        .set('isDeleting', false)
        .set('site', new DevelopmentSite()) as DevelopmentSiteViewState

    case ActionTypes.DeleteDueDiligenceItemFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as DevelopmentSiteViewState

    case ActionTypes.DeleteDueDiligenceItemRequest:
      return state.set('isDeleting', true) as DevelopmentSiteViewState

    case ActionTypes.DeleteDueDiligenceItemSuccess: {
      if (!state.dueDiligenceItems) {
        return state.set('isDeleting', false) as DevelopmentSiteViewState
      }

      const itemIndex = state.dueDiligenceItems.findIndex(
        (di) => di !== undefined && di.id === action.itemId,
      )

      if (itemIndex >= 0) {
        return state
          .set('isDeleting', false)
          .set(
            'dueDiligenceItems',
            state.dueDiligenceItems.delete(itemIndex),
          ) as DevelopmentSiteViewState
      } else {
        return state.set('isDeleting', false) as DevelopmentSiteViewState
      }
    }

    case ActionTypes.LoadCityProfileFailure:
      return state
        .set('error', action.error)
        .set('isLoadingCityProfile', false) as DevelopmentSiteViewState

    case ActionTypes.LoadCityProfileRequest:
      return state.set('isLoadingCityProfile', true) as DevelopmentSiteViewState

    case ActionTypes.LoadCityProfileSuccess:
      return state
        .set('error', undefined)
        .set('isLoadingCityProfile', false)
        .set('cityProfile', action.cityProfile) as DevelopmentSiteViewState

    case ActionTypes.LoadCriticalDatesDocumentFailure:
      return state
        .set('error', action.error)
        .set('isLoadingCriticalDatesDoc', false) as DevelopmentSiteViewState

    case ActionTypes.LoadCriticalDatesDocumentRequest:
      return state.set(
        'isLoadingCriticalDatesDoc',
        true,
      ) as DevelopmentSiteViewState

    case ActionTypes.LoadCriticalDatesDocumentSuccess:
      return state
        .set('error', undefined)
        .set('isLoadingCriticalDatesDoc', false)
        .set(
          'criticalDatesDoc',
          action.criticalDatesDoc,
        ) as DevelopmentSiteViewState

    case ActionTypes.LoadDevelopmentSiteFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as DevelopmentSiteViewState

    case ActionTypes.LoadDevelopmentSiteRequest:
      return state.set('isLoading', true) as DevelopmentSiteViewState

    case ActionTypes.LoadDevelopmentSiteSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('permissions', action.permissions)
        .set('site', action.site) as DevelopmentSiteViewState

    case ActionTypes.LoadDueDiligenceItemsFailure:
      return state
        .set('error', action.error)
        .set('isLoadingDueDiligenceItems', false) as DevelopmentSiteViewState

    case ActionTypes.LoadDueDiligenceItemsRequest:
      return state.set(
        'isLoadingDueDiligenceItems',
        true,
      ) as DevelopmentSiteViewState

    case ActionTypes.LoadDueDiligenceItemsSuccess:
      return state
        .set('error', undefined)
        .set('isLoadingDueDiligenceItems', false)
        .set(
          'dueDiligenceItems',
          action.dueDiligenceItems,
        ) as DevelopmentSiteViewState

    case ActionTypes.ResetState:
      return new DevelopmentSiteViewState()

    case DueDiligenceEditActionTypes.SaveDueDiligenceItemSuccess: {
      if (state.dueDiligenceItems !== undefined) {
        const itemIndex = state.dueDiligenceItems.findIndex(
          (di) => di !== undefined && di.id === action.item.id,
        )
        if (itemIndex >= 0) {
          return state.setIn(
            ['dueDiligenceItems', itemIndex],
            action.item,
          ) as DevelopmentSiteViewState
        } else {
          return state.set(
            'dueDiligenceItems',
            state.dueDiligenceItems.push(action.item),
          ) as DevelopmentSiteViewState
        }
      }

      return state
    }

    case ActionTypes.SetAttachments:
      return state.setIn(
        ['site', 'attachments'],
        action.value,
      ) as DevelopmentSiteViewState

    case ActionTypes.SetCityProfileAttachments:
      return state.setIn(
        ['cityProfile', 'attachments'],
        action.value,
      ) as DevelopmentSiteViewState

    case ActionTypes.ShareCriticalDatesFailure:
      return state
        .set('error', action.error)
        .set('isSharingCriticalDates', false) as DevelopmentSiteViewState

    case ActionTypes.ShareCriticalDatesRequest:
      return state.set(
        'isSharingCriticalDates',
        true,
      ) as DevelopmentSiteViewState

    case ActionTypes.ShareCriticalDatesSuccess:
      return state
        .set('error', undefined)
        .set('isSharingCriticalDates', false) as DevelopmentSiteViewState

    case ActionTypes.ShowShareCriticalDates:
      return state.set(
        'showShareCriticalDatesModal',
        true,
      ) as DevelopmentSiteViewState

    case ActionTypes.ToggleConfirmDelete:
      return state.set(
        'showConfirmDelete',
        action.show,
      ) as DevelopmentSiteViewState
    case ActionTypes.UpdateDueDiligenceItemFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as DevelopmentSiteViewState

    case ActionTypes.UpdateDueDiligenceItemRequest:
      if (!state.criticalDatesDoc) {
        return state.set('isLoading', false) as DevelopmentSiteViewState
      }

      const itemIndex = state.criticalDatesDoc.criticalDates.findIndex(
        (di) => di !== undefined && di.id === action.itemId,
      )

      return state
        .set('isLoading', true)
        .setIn(
          ['criticalDatesDoc', 'criticalDates', itemIndex, 'displayOrder'],
          action.index,
        ) as DevelopmentSiteViewState

    case ActionTypes.UpdateDueDiligenceItemSuccess: {
      if (!state.dueDiligenceItems) {
        return state.set('isLoading', false) as DevelopmentSiteViewState
      }

      return state.set('isLoading', false) as DevelopmentSiteViewState
    }
    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deleteDevelopmentSiteSaga(action: IDeleteDevelopmentSiteAction) {
  try {
    yield put(deleteDevelopmentSiteRequest())

    const reqUrl = `/api/DevelopmentClients/${action.clientId}/Sites/${action.siteId}`

    const result = yield makeAuthenticatedRequest(reqUrl, {
      method: RequestMethod.DELETE,
    })

    if (result.success === true) {
      yield put(push('/brokerage/pipeline'))
      yield put(deleteDevelopmentSiteSuccess())
    } else {
      yield put(deleteDevelopmentSiteFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteDevelopmentSiteFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* deleteDueDiligenceItemSaga(action: IDeleteDueDiligenceItemAction) {
  try {
    yield put(deleteDueDiligenceItemRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/DueDiligenceItems/${action.itemId}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(deleteDueDiligenceItemSuccess(action.itemId))
    } else {
      yield put(deleteDueDiligenceItemFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteDueDiligenceItemFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* updateDueDiligenceItemSaga(action: IUpdateDueDiligenceItemAction) {
  try {
    yield put(updateDueDiligenceItemRequest(action.index, action.itemId))

    const developmentSiteViewState = yield select((state: ApplicationState) => {
      return state.developmentSiteView as DevelopmentSiteViewState
    })

    const item = developmentSiteViewState.criticalDatesDoc.criticalDates
      .toArray()
      .filter((x: DueDiligenceItem) => x.id === action.itemId)[0]

    const result = yield makeAuthenticatedRequest(
      `/api/DevelopmentSites/${action.siteId}/DueDiligence/${action.itemId}`,
      {
        body: JSON.stringify(item.toJS()),
        method: RequestMethod.PUT,
      },
    )

    if (result.success === true) {
      yield put(
        updateDueDiligenceItemSuccess(
          action.index,
          action.itemId,
          action.siteId,
        ),
      )
    } else {
      yield put(updateDueDiligenceItemFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(updateDueDiligenceItemFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* getSiteRoutingSaga(action: IGetSiteRoutingAction) {
  try {
    const result = yield makeAuthenticatedRequest(
      `/api/developmentClients/client/sites/${action.siteId}/routing`,
    )

    if (result.success === true) {
      yield put(
        push(
          `/development/client/${result.routingParams.clientSlug}/site/${result.routingParams.siteSlug}`,
        ),
      )
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadCityProfileSaga(action: ILoadCityProfileAction) {
  try {
    yield put(loadCityProfileRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/CityProfiles/${action.stateFIPS}/${action.placeFIPS}`,
    )

    if (result.success === true) {
      yield put(loadCityProfileSuccess(CityProfile.fromJS(result.entity)))
    } else {
      yield put(loadCityProfileFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadCityProfileFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadCriticalDatesDocumentSaga(
  action: ILoadCriticalDatesDocumentAction,
) {
  try {
    yield put(loadCriticalDatesDocumentRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/DevelopmentSites/${action.siteId}/DueDiligence/CriticalDates`,
    )

    if (result.success === true) {
      yield put(
        loadCriticalDatesDocumentSuccess(
          DevelopmentCriticalDatesDocument.fromJS(result.entity),
        ),
      )
    } else {
      yield put(loadCriticalDatesDocumentFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadCriticalDatesDocumentFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadDueDiligenceItemsSaga(action: ILoadDueDiligenceItemsAction) {
  try {
    yield put(loadDueDiligenceItemsRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/DevelopmentSites/${action.siteId}/DueDiligence`,
    )

    if (result.success === true) {
      const dueDiligenceItems:
        | List<DueDiligenceItem>
        | undefined = Array.isArray(result.results)
        ? List(result.results.map((i: any) => DueDiligenceItem.fromJS(i)))
        : undefined

      yield put(loadDueDiligenceItemsSuccess(dueDiligenceItems))
    } else {
      yield put(loadDueDiligenceItemsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadDueDiligenceItemsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadDevelopmentSiteSaga(action: ILoadDevelopmentSiteAction) {
  try {
    yield put(loadDevelopmentSiteRequest())

    const clientResult = yield makeAuthenticatedRequest(
      `/api/DevelopmentClients/${action.clientSlug}`,
    )

    if (clientResult.success === true) {
      const siteResult = yield makeAuthenticatedRequest(
        `/api/DevelopmentClients/${clientResult.entity.id}/Sites/${action.siteSlug}`,
      )

      if (siteResult.success === true) {
        yield put(
          loadDevelopmentSiteSuccess(
            DevelopmentSite.fromJS(siteResult.entity),
            EntityPermissions.fromJS(siteResult.permissions),
          ),
        )
      } else {
        yield put(loadDevelopmentSiteFailure(siteResult.message))
        yield put(errorToast('Oops!', siteResult.message))
      }
    } else {
      yield put(loadDevelopmentSiteFailure(clientResult.message))
      yield put(errorToast('Oops!', clientResult.message))
    }
  } catch (err) {
    yield put(loadDevelopmentSiteFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* shareCriticalDatesSaga(action: IShareCriticalDatesAction) {
  try {
    yield put(shareCriticalDatesRequest())

    const shareUrl = `/api/DevelopmentSites/${action.siteId}/DueDiligence/CriticalDates/Share`

    const contactEmails = action.contacts
      .map((item: any) => {
        return item.addresses
      })
      .reduce((pre: any, current: any) => {
        return pre.concat(current)
      })

    const shareOpts = {
      body: JSON.stringify({
        comment: action.comment,
        contacts: contactEmails.toJS(),
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(shareUrl, shareOpts)

    if (result.success === true) {
      yield put(shareCriticalDatesSuccess())
      yield put(closeShareCriticalDates())
      yield put(
        successToast('Success!', 'Critical Dates Document has been shared'),
      )
    } else {
      yield put(shareCriticalDatesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(shareCriticalDatesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteDevelopmentSite() {
  yield takeLatest(ActionTypes.DeleteDevelopmentSite, deleteDevelopmentSiteSaga)
}

function* watchDeleteDueDiligenceItem() {
  yield takeEvery(
    ActionTypes.DeleteDueDiligenceItem,
    deleteDueDiligenceItemSaga,
  )
}

function* watchUpdateDueDiligenceItem() {
  yield takeEvery(
    ActionTypes.UpdateDueDiligenceItem,
    updateDueDiligenceItemSaga,
  )
}

function* watchGetSiteRouting() {
  yield takeLatest(ActionTypes.GetSiteRouting, getSiteRoutingSaga)
}

function* watchLoadCityProfile() {
  yield takeLatest(ActionTypes.LoadCityProfile, loadCityProfileSaga)
}

function* watchLoadCriticalDatesDocument() {
  yield takeLatest(
    ActionTypes.LoadCriticalDatesDocument,
    loadCriticalDatesDocumentSaga,
  )
}

function* watchLoadDevelopmentSite() {
  yield takeLatest(ActionTypes.LoadDevelopmentSite, loadDevelopmentSiteSaga)
}

function* watchLoadDueDiligenceItems() {
  yield takeLatest(ActionTypes.LoadDueDiligenceItems, loadDueDiligenceItemsSaga)
}

function* watchShareCriticalDates() {
  yield takeLatest(ActionTypes.ShareCriticalDates, shareCriticalDatesSaga)
}

export function* developmentSiteViewRootSaga() {
  yield fork(watchDeleteDevelopmentSite)
  yield fork(watchDeleteDueDiligenceItem)
  yield fork(watchUpdateDueDiligenceItem)
  yield fork(watchLoadCityProfile)
  yield fork(watchLoadCriticalDatesDocument)
  yield fork(watchLoadDevelopmentSite)
  yield fork(watchLoadDueDiligenceItems)
  yield fork(watchGetSiteRouting)
  yield fork(watchShareCriticalDates)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.developmentSiteView

export const selectCityProfile = () =>
  createSelector(selectModuleState(), (mState) => mState.cityProfile)

export const selectCriticalDatesDoc = () =>
  createSelector(selectModuleState(), (mState) => mState.criticalDatesDoc)

export const selectDueDiligenceItems = () =>
  createSelector(selectModuleState(), (mState) => mState.dueDiligenceItems)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsDeleting = () =>
  createSelector(selectModuleState(), (mState) => mState.isDeleting)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectIsLoadingCityProfile = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoadingCityProfile)

export const selectIsLoadingCriticalDatesDoc = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoadingCriticalDatesDoc,
  )

export const selectIsLoadingDueDiligenceItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoadingDueDiligenceItems,
  )

export const selectIsSharingCriticalDates = () =>
  createSelector(selectModuleState(), (mState) => mState.isSharingCriticalDates)

export const selectPermissions = () =>
  createSelector(selectModuleState(), (mState) => mState.permissions)

export const selectShowConfirmDelete = () =>
  createSelector(selectModuleState(), (mState) => mState.showConfirmDelete)

export const selectShowShareCriticalDatesModal = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.showShareCriticalDatesModal,
  )

export const selectSite = () =>
  createSelector(selectModuleState(), (mState) => mState.site)
